import React, { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Section, SectionHeading } from "../common/Section";
import Reveal from "../common/Reveal";
import {
  SERVICE_CATEGORIES,
  SERVICES,
  servicesByCategory,
} from "../../data/serviceCatalog";
import { SHOW_PUBLIC_PRICING } from "../../config/claims";

/**
 * Homepage services section.
 *
 * Driven entirely by src/data/serviceCatalog.js so every row links to a
 * service page that actually exists — this section is the homepage's main
 * route into the 25 service URLs. Categories are differentiated by
 * position, label and mono counts rather than by a gradient each.
 */

const pad = (n) => String(n).padStart(2, "0");

const ServicesPreview = () => {
  const [activeKey, setActiveKey] = useState(SERVICE_CATEGORIES[0].key);
  const tabRefs = useRef([]);

  // The catalog is static, so resolve each category's services once.
  const categories = useMemo(
    () =>
      SERVICE_CATEGORIES.map((category) => ({
        ...category,
        services: servicesByCategory(category.key),
      })),
    []
  );

  const activeIndex = categories.findIndex((c) => c.key === activeKey);
  const activeCategory = categories[activeIndex] ?? categories[0];

  // Vertical tablist keyboard support: arrows move and activate, Home/End jump.
  const handleTabKeyDown = (event, index) => {
    const count = categories.length;
    let next = null;

    if (event.key === "ArrowDown" || event.key === "ArrowRight") {
      next = (index + 1) % count;
    } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
      next = (index - 1 + count) % count;
    } else if (event.key === "Home") {
      next = 0;
    } else if (event.key === "End") {
      next = count - 1;
    }

    if (next === null) return;
    event.preventDefault();
    setActiveKey(categories[next].key);
    tabRefs.current[next]?.focus();
  };

  // The engineering grid goes on the section itself (as on TechnologyStack)
  // rather than on a 100vw absolute child: no horizontal overflow to clip, and
  // therefore no `overflow-hidden`, which would have made the sticky category
  // rail below inert.
  return (
    <Section tone="dark" id="services" className="grid-field-dark">
      {/* Header: left-aligned editorial heading, factual counts on the right */}
      <div className="relative grid gap-8 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end lg:gap-16">
        <SectionHeading
          tone="dark"
          eyebrow="What we do"
          title="Our Services"
          lead="The digital work we do, from AI integration through to full digital transformation. Four practices, and every service below opens its own page."
          maxWidth="max-w-2xl"
        />

        <Reveal
          delay={90}
          className="flex items-center gap-5 font-mono text-[0.7rem] uppercase tracking-eyebrow text-ink-500 lg:justify-end"
        >
          <span>
            <span className="text-white">{SERVICES.length}</span> services
          </span>
          <span aria-hidden="true" className="h-3 w-px bg-white/15" />
          <span>
            <span className="text-white">{pad(categories.length)}</span> practices
          </span>
        </Reveal>
      </div>

      <div className="relative mt-12 grid gap-10 lg:mt-16 lg:grid-cols-[minmax(0,15rem)_minmax(0,1fr)] lg:gap-14">
        {/* Category selector */}
        <Reveal className="lg:sticky lg:top-28 lg:self-start">
          <p className="font-mono text-[0.68rem] uppercase tracking-eyebrow text-ink-500">
            {pad(categories.length)} Practices
          </p>

          <div
            role="tablist"
            aria-label="Service categories"
            aria-orientation="vertical"
            className="mt-4"
          >
            {categories.map((category, index) => {
              const isActive = category.key === activeCategory.key;

              return (
                <button
                  key={category.key}
                  id={`services-tab-${category.key}`}
                  type="button"
                  role="tab"
                  ref={(node) => {
                    tabRefs.current[index] = node;
                  }}
                  aria-selected={isActive}
                  aria-controls="services-panel"
                  tabIndex={isActive ? 0 : -1}
                  onClick={() => setActiveKey(category.key)}
                  onKeyDown={(event) => handleTabKeyDown(event, index)}
                  className={`group block w-full border-l py-4 pl-4 text-left transition-colors duration-200 ${
                    isActive
                      ? "border-signal"
                      : "border-white/10 hover:border-white/30"
                  }`}
                >
                  <span className="flex gap-3">
                    <span
                      className={`w-5 shrink-0 pt-1 font-mono text-[0.68rem] transition-colors duration-200 ${
                        isActive ? "text-signal" : "text-ink-600"
                      }`}
                    >
                      {pad(index + 1)}
                    </span>

                    <span className="min-w-0 flex-1">
                      <span
                        className={`block font-bricolage text-base font-semibold leading-snug transition-colors duration-200 sm:text-lg ${
                          isActive
                            ? "text-white"
                            : "text-ink-300 group-hover:text-white"
                        }`}
                      >
                        {category.label}
                      </span>

                      <span className="mt-1.5 block font-mono text-[0.66rem] uppercase tracking-eyebrow text-ink-500">
                        {pad(category.services.length)} services
                      </span>

                      {isActive ? (
                        <span className="mt-2.5 block max-w-[38ch] text-sm leading-relaxed text-ink-400">
                          {category.blurb}
                        </span>
                      ) : null}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </Reveal>

        {/* Service index for the selected category */}
        <Reveal
          delay={90}
          id="services-panel"
          role="tabpanel"
          aria-labelledby={`services-tab-${activeCategory.key}`}
        >
          <ul
            key={activeCategory.key}
            className="border-b border-white/[0.08]"
            style={{ animation: "smoothFadeIn 320ms var(--dl-ease) both" }}
          >
            {activeCategory.services.map((service) => (
              <li key={service.path}>
                <Link
                  to={service.path}
                  className="group relative block border-t border-white/[0.08] py-5 transition-colors duration-200 hover:bg-white/[0.03] sm:py-6"
                >
                  {/* Left-edge rule on hover, instead of lifting a card */}
                  <span
                    aria-hidden="true"
                    className="absolute left-0 top-0 h-full w-px origin-top scale-y-0 bg-signal transition-transform duration-300 ease-out-expo group-hover:scale-y-100 group-focus-visible:scale-y-100"
                  />

                  <div className="grid gap-x-8 gap-y-3 pl-4 pr-1 transition-transform duration-200 ease-out-expo group-hover:translate-x-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:pl-5">
                    <div className="min-w-0">
                      <h3 className="flex items-start gap-2 font-bricolage text-lg font-semibold text-white sm:text-xl">
                        <span className="text-balance">{service.title}</span>
                        <ArrowUpRight
                          aria-hidden="true"
                          className="mt-1 h-4 w-4 shrink-0 text-ink-600 transition-[color,transform] duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-signal"
                        />
                      </h3>

                      <p className="mt-2 max-w-[60ch] text-sm leading-relaxed text-ink-400">
                        {service.description}
                      </p>

                      {/* Real technologies, straight from the service data */}
                      <ul className="mt-3 flex flex-wrap items-center gap-1.5">
                        {service.technologies.slice(0, 3).map((tech) => (
                          <li
                            key={tech}
                            className="rounded border border-white/10 px-2 py-0.5 font-mono text-[0.66rem] text-ink-400 transition-colors duration-200 group-hover:border-white/20 group-hover:text-ink-300"
                          >
                            {tech}
                          </li>
                        ))}
                        {service.technologies.length > 3 ? (
                          <li className="px-1 font-mono text-[0.66rem] text-ink-500">
                            +{service.technologies.length - 3}
                          </li>
                        ) : null}
                      </ul>
                    </div>

                    <div className="flex items-center gap-3 font-mono text-[0.66rem] text-ink-500 sm:flex-col sm:items-end sm:gap-1.5 sm:pt-1">
                      <span className="uppercase tracking-eyebrow">
                        {service.duration}
                      </span>
                      {SHOW_PUBLIC_PRICING && service.price ? (
                        <span className="text-ink-400">{service.price}</span>
                      ) : null}
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>

          <Link
            to="/services"
            className="group mt-6 inline-flex items-center gap-3 font-mono text-[0.7rem] uppercase tracking-eyebrow text-ink-300 transition-colors duration-200 hover:text-white"
          >
            View all {SERVICES.length} services
            <ArrowRight
              aria-hidden="true"
              className="h-4 w-4 text-ink-500 transition-[color,transform] duration-200 group-hover:translate-x-1 group-hover:text-signal"
            />
          </Link>
        </Reveal>
      </div>
    </Section>
  );
};

export default ServicesPreview;
